import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable } from 'rxjs/Observable';
import { User } from '../user/user';
import { BehaviorSubject } from 'rxjs/BehaviorSubject';
import { environment } from '../../environments/environment';

const httpOptions = {
  headers: new HttpHeaders({ 'Content-Type': 'application/json' })
};

@Injectable()
export class AuthService {
  private URL: string = environment.apiUrl;
  public role: string;
  public userId: string;
  public email: string;
  private user: User;
  public authToSave: boolean = false;

  public token: string;
  public isAuthorization = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient) {
    // Recovering the token from sessionStorage when initializing the service
    this.restoreAuthFromStorage();
  }

  login(email: string, password: string): Observable<any> {
    return this.http.post(`${this.URL}/login`, { email, password }, httpOptions);
  }

  getUserById(id: string, token: string): Observable<any> {
    return this.http.get(`${this.URL}/users/${id}?access_token=${token}`, httpOptions);
  }

  register(user: User): Observable<any> {
    return this.http.post(`${this.URL}/users`, user, httpOptions);
  }

  updateUser(user: User): Observable<any> {
    const userId = user.id;
    // console.log('updateUser user', user);
    const newUser = {...user};
    delete newUser.id;
    return this.http.patch(
      `${this.URL}/users/${userId}?access_token=${this.token}`,
      newUser,
      httpOptions
    );
  }

  logout() {
    if (this.token) {
      this.http.post(`${this.URL}/users/logout?access_token=${this.token}`, null, httpOptions);
    }
    this.clearAuthorization();
    sessionStorage.removeItem('token');
    sessionStorage.removeItem('isAuthorization');
  }

  resetPassword(email: string): Observable<any> {
    return this.http.post(`${this.URL}/users/reset`, { email: email }, httpOptions);
  }

  setUser(user: User): void {
    this.user = user;
    // console.log('this.user', this.user);
  }

  getUser(): User {
    return this.user;
  }

  setToken(token: string) {
    this.token = token;
  }

  getToken(): string {
    if (this.token) {
      return this.token;
    }

    try {
      const tokenData = sessionStorage.getItem('token');
      if (tokenData) {
        const tokenObj = JSON.parse(tokenData);
        if (tokenObj && tokenObj.id) {
          this.token = tokenObj.id;
          return this.token;
        }
      }
    } catch (error) {
      return '';
    }

    return '';
  }

  isAuthenticated(): boolean {
    return !!this.getToken() && this.isAuthorization.value;
  }

  setUserId(id: string) {
    this.userId = id;
  }

  getUserId() {
    return this.userId;
  }

  setRole(name: string) {
    // console.log(name);
    this.role = name;
  }

  getRole() {
    return this.role;
  }

  resolveRoleFromToken(): string | null {
    if (this.role) {
      return this.role;
    }

    const roleFromToken = this.readRoleFromStoredToken();
    if (roleFromToken) {
      this.setRole(roleFromToken);
      return roleFromToken;
    }

    return null;
  }

  private readRoleFromStoredToken(): string | null {
    try {
      const tokenData = sessionStorage.getItem('token');
      if (!tokenData) {
        return null;
      }

      const tokenObj = JSON.parse(tokenData);
      if (tokenObj.user && tokenObj.user.role && tokenObj.user.role.name) {
        return tokenObj.user.role.name;
      }

      if (tokenObj.role && tokenObj.role.name) {
        return tokenObj.role.name;
      }
    } catch (error) {
      return null;
    }

    return null;
  }

  setEmail(em: string) {
    this.email = em;
  }

  getEmail() {
    return this.email;
  }

  setIsAuthorization(value: boolean) {
    this.isAuthorization.next(value);
  }

  clearAuthorization() {
    this.role = '';
    this.email = '';
    this.userId = '';
    this.token = '';
    this.user = null;
    this.isAuthorization.next(false);
  }

  triggerAuthToSave() {
    if (this.authToSave) {
      this.authToSave = false;
    } else {
      this.authToSave = true;
    }
  }

  private restoreAuthFromStorage() {
    try {
      const tokenData = sessionStorage.getItem('token');
      const isAuthData = sessionStorage.getItem('isAuthorization');
      
      if (tokenData && isAuthData) {
        const tokenObj = JSON.parse(tokenData);
        const isAuth = JSON.parse(isAuthData);
        
        if (tokenObj && tokenObj.id) {
          this.token = tokenObj.id;
          this.userId = tokenObj.userId || '';
          this.user = tokenObj.user || null;
          this.email = tokenObj.user && tokenObj.user.email ? tokenObj.user.email : '';
          this.role = this.readRoleFromStoredToken() || '';
          this.isAuthorization.next(isAuth);
          
          console.log('AuthService: Token restored from sessionStorage');
        }
      }
    } catch (error) {
      console.warn('AuthService: Error restoring auth from sessionStorage:', error);
      // Cleaning up damaged data
      sessionStorage.removeItem('token');
      sessionStorage.removeItem('isAuthorization');
    }
  }
}



// WEBPACK FOOTER //
// ./src/app/services/auth.service.ts